import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
var _String;
/* eslint-disable @helpers/no-then-catch-finally */ import { Config } from '@core/configuration';
import { CurrentOrganization } from '@core/organization';
import { cleanupSearchParams } from '@core/routes';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { AuthenticationEvents } from './constants/AuthenticationEvents.js';
import { GlobalEvents } from './constants/GlobalEvents.js';
import { AuthenticationTracking } from './tracking/AuthenticationTracking.js';
import { AccessTokenUtils } from './utils/AccessTokenUtils.js';
import { getCleanRedirectURI } from './utils/getCleanRedirectURI.js';
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
var OAUTH2_ERROR_PARAM = 'error=';
var tokenExpirationTimer;
export var tokenRenewalProps = {
    promptId: 'token-renewal-prompt',
    verificationDelay: dayjs.duration(1, 'minutes'),
    threshold: dayjs.duration(15, 'minutes'),
    windowWidth: 600,
    windowHeight: 650
};
export var startTokenExpirationTimer = function startTokenExpirationTimer() {
    tokenExpirationTimer = window.setInterval(verifyTokenExpiration, tokenRenewalProps.verificationDelay.asMilliseconds());
};
export var doLogout = function doLogout() {
    var storedAccessTokenBeforeClearing = AccessTokenUtils.getToken();
    clearAccessTokenAndCache();
    // Do a logout on the platform as well, and continue to a new login or specified page.
    AuthenticationTracking.trackLogout();
    sendLogoutRequest(storedAccessTokenBeforeClearing);
};
export var clearAccessToken = function clearAccessToken() {
    clearTokenExpirationTimeout();
    AccessTokenUtils.clear();
};
export var doAuthorizeUser = function doAuthorizeUser() {
    return _async_to_generator(function() {
        var redirectUrl, urlWithoutHash, hashWithoutNewSession, urlParams, authorizeUrl;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    clearAccessTokenAndCache();
                    if (window.location.href.includes(OAUTH2_ERROR_PARAM)) {
                        clearOAuth2Error();
                        doLogout();
                        return [
                            2
                        ];
                    }
                    redirectUrl = window.location.href;
                    // If we are here because of the /#newsession route, use the base admin URL as redirect to prevent redirection loop.
                    if (window.location.hash.includes('newsession')) {
                        urlWithoutHash = window.location.href.replace(window.location.hash, '');
                        hashWithoutNewSession = window.location.hash.replace('newsession', '');
                        redirectUrl = "".concat(urlWithoutHash).concat(hashWithoutNewSession);
                    }
                    urlParams = new URLSearchParams(window.location.search);
                    if (!urlParams.get('code')) return [
                        3,
                        2
                    ];
                    // fetch the code and exchange it for an access token
                    return [
                        4,
                        fetchAccessToken(urlParams)
                    ];
                case 1:
                    _state.sent();
                    return [
                        3,
                        4
                    ];
                case 2:
                    return [
                        4,
                        getAuthorizeUrl(redirectUrl)
                    ];
                case 3:
                    authorizeUrl = _state.sent();
                    window.location.assign(authorizeUrl);
                    _state.label = 4;
                case 4:
                    return [
                        2
                    ];
            }
        });
    })();
};
export var getTokenRenewalParams = function getTokenRenewalParams(provider) {
    return _async_to_generator(function() {
        var redirectUri, authorizeOptions, url, params;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    redirectUri = "".concat(window.location.origin).concat(window.location.pathname.replace('/index.html', '/'), "platformOauthRedirectHandler.html");
                    authorizeOptions = {
                        provider: provider,
                        skipLoginPage: provider === 'SAML'
                    };
                    return [
                        4,
                        getAuthorizeUrl(redirectUri, authorizeOptions)
                    ];
                case 1:
                    url = _state.sent();
                    params = {
                        url: url,
                        w: tokenRenewalProps.windowWidth,
                        h: tokenRenewalProps.windowHeight
                    };
                    return [
                        2,
                        params
                    ];
            }
        });
    })();
};
/* * * * * * * * * * * * * *
 *   Private functions
 * * * * * * * * * ** * * * */ var clearOAuth2Error = function clearOAuth2Error() {
    window.location.href = window.location.href.replace(new RegExp("&?".concat(OAUTH2_ERROR_PARAM, ".*"), 'i'), '');
};
var clearTokenExpirationTimeout = function clearTokenExpirationTimeout() {
    clearInterval(tokenExpirationTimer);
};
var verifyTokenExpiration = function verifyTokenExpiration() {
    var expiration = dayjs(AccessTokenUtils.getExpiration());
    var now = dayjs();
    var diff = dayjs.duration(expiration.diff(now));
    var isTokenStillActive = dayjs(expiration).isSameOrAfter(now);
    var needRenewal = diff.asMilliseconds() <= tokenRenewalProps.threshold.asMilliseconds();
    if (isTokenStillActive && needRenewal) {
        clearTokenExpirationTimeout();
        var event = new Event(AuthenticationEvents.ShowTokenRenewalPrompt, {
            bubbles: true
        });
        document.dispatchEvent(event);
    }
};
var clearAccessTokenAndCache = function clearAccessTokenAndCache() {
    var event = new Event(GlobalEvents.AppIsUnloading, {
        bubbles: true
    });
    document.dispatchEvent(event);
    clearAccessToken();
    localStorage.removeItem('backboneCache');
};
var sendLogoutRequest = function sendLogoutRequest(accessToken) {
    return _async_to_generator(function() {
        var logoutUrl, search, _, _tmp;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    logoutUrl = new URL('/logout', Config.CloudPlatform.url);
                    _ = URLSearchParams.bind;
                    _tmp = {
                        organizationId: CurrentOrganization.getId()
                    };
                    return [
                        4,
                        getAuthorizeUrl("".concat(window.location.origin).concat(window.location.pathname).concat(window.location.search))
                    ];
                case 1:
                    search = new (_.apply(URLSearchParams, [
                        void 0,
                        cleanupSearchParams.apply(void 0, [
                            (_tmp.continue = _state.sent(), _tmp)
                        ])
                    ]));
                    logoutUrl.search = search.toString();
                    fetch(logoutUrl.toString(), {
                        method: 'GET',
                        headers: {
                            Authorization: "Bearer ".concat(accessToken)
                        },
                        redirect: 'manual'
                    }).then(function(response) {
                        window.location.assign(response.url);
                    }).catch(function(error) {
                        console.error(error);
                    });
                    return [
                        2
                    ];
            }
        });
    })();
};
export var fetchAccessToken = function fetchAccessToken(urlSearchParams) {
    return _async_to_generator(function() {
        var _localStorage_getItem, code, codeVerifier, fetchTokenUrl, credentials, redirectUri, body, response, token, unused;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    code = urlSearchParams.get('code');
                    codeVerifier = localStorage.getItem('code_verifier');
                    fetchTokenUrl = new URL(Config.CloudPlatform.tokenPath, Config.CloudPlatform.url);
                    credentials = btoa("".concat(Config.app.clientId, ":").concat(Config.app.clientId));
                    redirectUri = getCleanRedirectURI((_localStorage_getItem = localStorage.getItem('redirect_uri')) !== null && _localStorage_getItem !== void 0 ? _localStorage_getItem : window.location.href);
                    body = new URLSearchParams({
                        grant_type: 'authorization_code',
                        code: code,
                        redirect_uri: redirectUri,
                        client_id: Config.app.clientId,
                        code_verifier: codeVerifier
                    });
                    _state.label = 1;
                case 1:
                    _state.trys.push([
                        1,
                        4,
                        5,
                        6
                    ]);
                    return [
                        4,
                        fetch(fetchTokenUrl, {
                            method: 'POST',
                            headers: {
                                Authorization: "Basic ".concat(credentials)
                            },
                            body: body
                        })
                    ];
                case 2:
                    response = _state.sent();
                    if (!response.ok) {
                        throw new Error("HTTP error: ".concat(response.status));
                    }
                    return [
                        4,
                        response.json()
                    ];
                case 3:
                    token = _state.sent();
                    AccessTokenUtils.setToken(token.access_token, token.expires_in);
                    window.location.replace(localStorage.getItem('redirect_uri'));
                    return [
                        3,
                        6
                    ];
                case 4:
                    unused = _state.sent();
                    window.location.href = redirectUri;
                    return [
                        3,
                        6
                    ];
                case 5:
                    localStorage.removeItem('code_verifier');
                    localStorage.removeItem('redirect_uri');
                    return [
                        7
                    ];
                case 6:
                    return [
                        2
                    ];
            }
        });
    })();
};
var AccessTokenScopes = {
    Full: 'full'
};
var generateCodeVerifier = function generateCodeVerifier() {
    var codeLength = 64;
    var array = new Uint8Array(codeLength);
    crypto.getRandomValues(array);
    return base64UrlEncode(array);
};
var generateCodeChallenge = function generateCodeChallenge(codeVerifier) {
    return _async_to_generator(function() {
        var encoder, data, digest;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    encoder = new TextEncoder();
                    data = encoder.encode(codeVerifier);
                    return [
                        4,
                        crypto.subtle.digest('SHA-256', data)
                    ];
                case 1:
                    digest = _state.sent();
                    return [
                        2,
                        base64UrlEncode(new Uint8Array(digest))
                    ];
            }
        });
    })();
};
var base64UrlEncode = function base64UrlEncode(array) {
    return btoa((_String = String).fromCharCode.apply(_String, _to_consumable_array(array))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};
var getAuthorizeUrl = function getAuthorizeUrl(redirectUri, queryParams) {
    return _async_to_generator(function() {
        var orgId, redirectURL, codeVerifier, codeChalenge, search, authorizeUrl;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    orgId = CurrentOrganization.getId();
                    redirectURL = getCleanRedirectURI(redirectUri);
                    codeVerifier = generateCodeVerifier();
                    localStorage.setItem('code_verifier', codeVerifier);
                    localStorage.setItem('redirect_uri', redirectURL);
                    return [
                        4,
                        generateCodeChallenge(codeVerifier)
                    ];
                case 1:
                    codeChalenge = _state.sent();
                    search = new URLSearchParams(cleanupSearchParams(_object_spread({
                        client_id: Config.app.clientId,
                        response_type: Config.CloudPlatform.responseType,
                        redirect_uri: redirectURL,
                        scope: Object.values(AccessTokenScopes).join(' '),
                        organizationId: orgId === 'newsession' ? undefined : orgId,
                        code_challenge: codeChalenge,
                        code_challenge_method: 'S256'
                    }, queryParams)));
                    authorizeUrl = new URL(Config.CloudPlatform.authorizePath, Config.CloudPlatform.url);
                    authorizeUrl.search = search.toString();
                    return [
                        2,
                        authorizeUrl.toString()
                    ];
            }
        });
    })();
};

//# sourceMappingURL=Authentication.js.map